import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorText, mediaUrl } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { Switch } from "../components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { ProjectForm } from "./ProjectForm";

export const ProjectsTab = () => {
  const { reload } = useSite();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/projects");
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  useEffect(() => { load(); }, []);
  const refresh = () => { load(); reload(); };

  const patch = async (p, changes) => {
    const { id, order, ...body } = { ...p, ...changes };
    await api.put(`/admin/projects/${p.id}`, body).catch((e) => toast.error(errorText(e)));
    refresh();
  };
  const move = async (i, dir) => {
    const ids = items.map((p) => p.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await api.post("/admin/projects/reorder", { ids });
    refresh();
  };
  const remove = async () => {
    await api.delete(`/admin/projects/${toDelete.id}`);
    toast.success("Project deleted");
    setToDelete(null);
    refresh();
  };

  return (
    <div className="border border-line bg-ivory" data-testid="admin-projects-tab">
      <div className="flex items-center justify-between border-b border-line p-5">
        <div>
          <h2 className="font-serif text-2xl text-wine">Portfolio projects</h2>
          <p className="text-xs text-muted-ink">Use the arrows to change the order. Featured projects (up to 6) appear on Home.</p>
        </div>
        <button onClick={() => { setEditing(null); setOpen(true); }} className="btn btn-primary !px-4 !py-2.5" data-testid="admin-add-project-btn"><Plus size={14} /> Add</button>
      </div>
      <ul>
        {items.map((p, i) => (
          <li key={p.id} className="flex flex-wrap items-center gap-4 border-b border-line p-4 last:border-0" data-testid={`admin-project-row-${p.slug}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} aria-label="Move up" className="p-1 text-muted-ink hover:text-wine"><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} aria-label="Move down" className="p-1 text-muted-ink hover:text-wine"><ArrowDown size={14} /></button>
            </div>
            <img src={mediaUrl(p.cover_image)} alt="" className="h-16 w-12 object-cover" />
            <div className="min-w-[150px] flex-1">
              <p className="font-serif text-xl text-charcoal">{p.title}</p>
              <p className="text-xs text-muted-ink">{p.category} · {p.label} · {p.year}</p>
            </div>
            <label className="flex items-center gap-2 text-xs"><Switch checked={p.visible} onCheckedChange={(v) => patch(p, { visible: v })} data-testid={`admin-project-visible-${p.slug}`} /> Visible</label>
            <label className="flex items-center gap-2 text-xs"><Switch checked={p.featured} onCheckedChange={(v) => patch(p, { featured: v })} data-testid={`admin-project-featured-${p.slug}`} /> Featured</label>
            <button onClick={() => { setEditing(p); setOpen(true); }} className="p-2 text-wine" aria-label="Edit" data-testid={`admin-project-edit-${p.slug}`}><Pencil size={16} /></button>
            <button onClick={() => setToDelete(p)} className="p-2 text-muted-ink hover:text-wine" aria-label="Delete" data-testid={`admin-project-delete-${p.slug}`}><Trash2 size={16} /></button>
          </li>
        ))}
        {!items.length && <li className="p-10 text-center text-sm text-muted-ink">No projects yet — add your first one.</li>}
      </ul>
      <ProjectForm open={open} project={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); refresh(); }} />
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-ivory">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl font-normal text-wine">Delete “{toDelete?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>This removes the project from the website permanently. To keep it but hide it, switch off “Visible” instead.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-wine" data-testid="admin-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
